import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Play, Coffee, CalendarDays, Sunrise, Sun, Moon, Sparkles } from 'lucide-react';
import StreakBadge from '../streak/StreakBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 6) return { text: 'Aún es temprano', Icon: Moon };
  if (h < 12) return { text: 'Buenos días', Icon: Sunrise };
  if (h < 18) return { text: 'Buenas tardes', Icon: Sun };
  return { text: 'Buenas noches', Icon: Moon };
};

/**
 * DailyHero — bloque hero personal en top del Dashboard.
 *
 * Elementos:
 *  - Saludo personalizado + hora del día.
 *  - StreakBadge con flama pulsante.
 *  - Card grande con el foco del día:
 *     · Si hay workout pendiente → botón GIGANTE naranja "ENTRENAR HOY"
 *     · Si está in_progress → botón cyan "CONTINUAR ENTRENAMIENTO"
 *     · Si es rest day → mensaje motivacional + botón secundario "Ver calendario"
 *     · Si completado → mensaje de felicitación
 *     · Si no hay plan/día → mensaje "Genera tu calendario"
 *
 * Diseñado para ser lo primero que ve el usuario al abrir el dashboard,
 * pensado para generar acción, no solo mostrar datos.
 */
const DailyHero = ({ userName, planInProgress = false }) => {
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recovering, setRecovering] = useState(false);
  const navigate = useNavigate();
  const greeting = getGreeting();
  const GreetIcon = greeting.Icon;
  const firstName = (userName || '').split(' ')[0] || 'Atleta';

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/calendar/today`)
      .then((res) => {
        if (!cancelled) setToday(res.data);
      })
      .catch(() => {
        if (!cancelled) setToday(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
  }, []);

  const day = today?.today;
  const workoutDetails = today?.workout_details;
  const status = day?.status;
  const isRest = day?.is_rest_day;
  const hasWorkout = today?.has_workout;
  const recovery = today?.recovery;

  // Recuperar hoy un entrenamiento que quedó sin hacer (hoy pasa a ser el siguiente día del plan).
  const recoverToday = async () => {
    setRecovering(true);
    try {
      const res = await axios.post(`${API}/calendar/recover-today`);
      if (res.data?.day?.id) navigate(`/app/workout/run/${res.data.day.id}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'No se pudo recuperar el entrenamiento');
      setRecovering(false);
    }
  };

  // Config del CTA principal según estado
  let cta = null;
  if (today?.training_block?.active) {
    // F5: reporte de lesión/incapacidad activo → el entrenamiento guiado está en pausa
    cta = {
      variant: 'blocked',
      title: 'Entrenamiento en pausa',
      subtitle: `Por tu reporte: ${today.training_block.reason}. Tu coach o el equipo ONOR te avisará cuando puedas volver.`,
      buttonLabel: 'Ver mi reporte',
      buttonHref: '/app/reports',
      icon: CalendarDays,
      color: 'slate',
    };
  } else if (planInProgress) {
    cta = {
      variant: 'notice',
      title: 'Preparando tu plan',
      subtitle: 'Estamos personalizando tu entrenamiento.',
      buttonLabel: 'Explorar mientras tanto',
      buttonHref: '/app/hub',
      icon: Sparkles,
      color: 'cyan',
    };
  } else if (loading) {
    cta = null;
  } else if (recovery && (!day || isRest)) {
    cta = {
      variant: 'recover',
      title: 'Ayer no entrenaste',
      subtitle: `Puedes recuperarlo hoy: ${recovery.next_day?.workout_day_name || 'tu siguiente entrenamiento'}. El resto de la semana se acomoda solo.`,
      buttonLabel: recovering ? 'Preparando…' : '[Recuperar hoy]',
      onClick: recoverToday,
      icon: Play,
      color: 'orange',
    };
  } else if (!day) {
    cta = {
      variant: 'setup',
      title: 'Aún no tienes calendario',
      subtitle: 'Configura tus días de entrenamiento para arrancar.',
      buttonLabel: 'Configurar calendario',
      buttonHref: '/app/calendar',
      icon: CalendarDays,
      color: 'orange',
    };
  } else if (isRest) {
    cta = {
      variant: 'rest',
      title: 'Día de descanso',
      subtitle: 'La recuperación también es entrenamiento.',
      buttonLabel: 'Ver semana',
      buttonHref: '/app/calendar',
      icon: Coffee,
      color: 'slate',
    };
  } else if (status === 'completed') {
    cta = {
      variant: 'done',
      title: '¡Hoy ya lo diste!',
      subtitle: 'Revisa tu resumen y prepárate para mañana.',
      buttonLabel: 'Ver resumen',
      buttonHref: day?.session_id ? `/app/hub/history/${day.session_id}` : '/app/calendar',
      icon: Sparkles,
      color: 'emerald',
    };
  } else if (status === 'in_progress') {
    cta = {
      variant: 'resume',
      title: 'Tienes un entrenamiento en curso',
      subtitle: workoutDetails?.day_name || day?.workout_day_name || 'Continúa donde lo dejaste.',
      buttonLabel: '[Continuar]',
      buttonHref: `/app/workout/run/${day.id}`,
      icon: Play,
      color: 'cyan',
    };
  } else if (status === 'pending' && hasWorkout) {
    cta = {
      variant: 'go',
      title: workoutDetails?.day_name || day?.workout_day_name || 'Entrenamiento del día',
      subtitle:
        workoutDetails?.focus ||
        day?.workout_focus ||
        'Vamos por él. Tu plan lo pide.',
      duration: workoutDetails?.estimated_duration_min,
      buttonLabel: '[Entrenar ahora]',
      buttonHref: `/app/workout/run/${day.id}`,
      icon: Play,
      color: 'orange',
    };
  } else {
    cta = {
      variant: 'idle',
      title: 'Hoy no tienes entrenamiento programado',
      subtitle: 'Aprovecha para revisar tu progreso.',
      buttonLabel: 'Ver métricas',
      buttonHref: '/app/progress/weekly',
      icon: Sparkles,
      color: 'slate',
    };
  }

  const ctaButtonClass =
    cta?.color === 'orange'
      ? 'btn-primary text-xl md:text-2xl h-14 md:h-16 px-8 md:px-10'
      : cta?.color === 'cyan'
      ? 'bg-primary hover:bg-primary/90 text-white uppercase tracking-wider font-bold font-display text-xl h-14 px-8 rounded-sm transition-all duration-fast ease-out-atl active:scale-[0.97] inline-flex items-center'
      : cta?.color === 'emerald'
      ? 'btn-accent text-lg h-12 px-4'
      : 'btn-secondary text-lg h-12 px-4';

  const CTAIcon = cta?.icon;

  return (
    <div
      className="relative overflow-hidden rounded-lg border border-zinc-600 bg-zinc-800 p-5 md:p-8 animate-slide-up tx relief-deep"
      data-testid="daily-hero"
    >
      {/* Identidad v2.1: esquinas de corchete y destello de acero (sin glows). */}
      <i className="corner corner-tl" aria-hidden="true" />
      <i className="corner corner-br" aria-hidden="true" />
      <i className="onor-glint" aria-hidden="true" style={{ animationDuration: '9s', animationDelay: '2s', opacity: .6 }} />

      {/* Header: saludo + streak */}
      <div className="relative flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <GreetIcon className="w-4 h-4 text-primary" />
          <p className="text-slate-400 text-sm">
            {greeting.text}, <span className="text-white font-medium">{firstName}</span>
          </p>
        </div>
        <StreakBadge />
      </div>

      {/* Título + subtítulo del CTA */}
      {cta && (
        <div className="relative space-y-4">
          <div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl md:text-6xl text-white uppercase leading-tight tracking-tight [text-wrap:balance]">
              {cta.title}
            </h2>
            <p className="text-slate-300 text-base md:text-lg mt-3 max-w-2xl">
              {cta.subtitle}
              {cta.duration && (
                <span className="ml-2 inline-flex items-center gap-1 text-slate-400 text-sm">
                  · <span className="tabular-nums">{cta.duration} min</span>
                </span>
              )}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {cta.onClick ? (
              <button type="button" className={ctaButtonClass} onClick={cta.onClick} disabled={recovering} data-testid="hero-recover-btn">
                {CTAIcon && <CTAIcon className="w-6 h-6 mr-2" />}
                {cta.buttonLabel}
              </button>
            ) : (
              <Link to={cta.buttonHref}>
                <button className={ctaButtonClass}>
                  {CTAIcon && <CTAIcon className="w-6 h-6 mr-2" />}
                  {cta.buttonLabel}
                </button>
              </Link>
            )}
            {(cta.variant === 'go' || cta.variant === 'resume' || cta.variant === 'recover') && (
              <Link
                to="/app/calendar"
                className="text-slate-400 hover:text-white text-sm underline-offset-4 hover:underline transition-colors"
              >
                Ver toda la semana
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyHero;
